import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  APP_STORE_LOCALIZATIONS,
  getLocaleFlag,
} from '../constants/appStoreLocalizations';

export type LocaleOption = { value: string; label: string };

export type TargetLocaleChecklistProps = {
  selected: string[];
  onChange: (locales: string[]) => void;
  /** Override catalog; defaults to APP_STORE_LOCALIZATIONS. */
  locales?: LocaleOption[];
  /** Locales hidden from the list (typically the source / primary). */
  excludeLocales?: string[];
  /** Defaults to `appScreen.targetLanguages`. Pass empty string to hide. */
  label?: string;
  showSelectAll?: boolean;
  showClearAll?: boolean;
  disabled?: boolean;
  className?: string;
  /** Applied to the checkbox grid (e.g. max-h-56 overflow-y-auto). */
  listClassName?: string;
};

/**
 * Standard App Store target-locale checklist (App Screens AI Translation pattern):
 * label + Select all | Clear all, then 2-col bordered checkbox cards with flag + label.
 */
export const TargetLocaleChecklist: React.FC<TargetLocaleChecklistProps> = ({
  selected,
  onChange,
  locales = APP_STORE_LOCALIZATIONS,
  excludeLocales = [],
  label,
  showSelectAll = true,
  showClearAll = true,
  disabled = false,
  className,
  listClassName,
}) => {
  const { t } = useTranslation();
  const exclude = new Set(excludeLocales.filter(Boolean));
  const options = locales.filter(loc => !exclude.has(loc.value));
  const optionValues = options.map(l => l.value);
  const heading = label === undefined ? t('appScreen.targetLanguages') : label;

  const optionSet = new Set(optionValues);

  const toggle = (locale: string) => {
    if (disabled) return;
    onChange(
      selected.includes(locale)
        ? selected.filter(l => l !== locale)
        : [...selected, locale]
    );
  };

  /** Only mutates locales visible in this list; preserves other selected values. */
  const selectAllVisible = () => {
    const next = new Set(selected);
    optionValues.forEach(v => next.add(v));
    onChange(Array.from(next));
  };

  const clearVisible = () => {
    onChange(selected.filter(l => !optionSet.has(l)));
  };

  return (
    <div className={className}>
      {(heading || showSelectAll || showClearAll) && (
        <div className="flex items-center justify-between gap-2 mb-2">
          {heading ? (
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {heading}
            </label>
          ) : (
            <span />
          )}
          {(showSelectAll || showClearAll) && (
            <div className="flex gap-2">
              {showSelectAll && (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={selectAllVisible}
                  className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50"
                >
                  {t('panel.selectAll')}
                </button>
              )}
              {showSelectAll && showClearAll && (
                <span className="text-gray-300 dark:text-gray-600">|</span>
              )}
              {showClearAll && (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={clearVisible}
                  className="text-xs font-medium text-gray-600 dark:text-gray-400 hover:underline disabled:opacity-50"
                >
                  {t('appScreen.clearAll')}
                </button>
              )}
            </div>
          )}
        </div>
      )}
      <div className={clsx('grid grid-cols-1 sm:grid-cols-2 gap-2', listClassName)}>
        {options.map(loc => {
          const checked = selected.includes(loc.value);
          return (
            <label
              key={loc.value}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors',
                disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                checked
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-500'
                  : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              )}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => toggle(loc.value)}
                className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-900 dark:text-white">
                {getLocaleFlag(loc.value)} {loc.label}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export type SourceTargetLocalePickerProps = {
  sourceLocale: string;
  onSourceChange: (locale: string) => void;
  targetLocales: string[];
  onTargetLocalesChange: (locales: string[]) => void;
  /** Override catalog; defaults to APP_STORE_LOCALIZATIONS. */
  locales?: LocaleOption[];
  /** When false, only the target checklist is shown. Default true. */
  showSource?: boolean;
  sourceSelectId?: string;
  disabled?: boolean;
  className?: string;
  targetListClassName?: string;
};

/**
 * Standard source + target App Store locale picker used in translation / locale modals.
 * Changing source automatically drops that locale from the target selection.
 */
const SourceTargetLocalePicker: React.FC<SourceTargetLocalePickerProps> = ({
  sourceLocale,
  onSourceChange,
  targetLocales,
  onTargetLocalesChange,
  locales = APP_STORE_LOCALIZATIONS,
  showSource = true,
  sourceSelectId,
  disabled = false,
  className,
  targetListClassName,
}) => {
  const { t } = useTranslation();
  const autoId = useId();
  const selectId = sourceSelectId ?? autoId;

  const handleSourceChange = (next: string) => {
    onSourceChange(next);
    if (targetLocales.includes(next)) {
      onTargetLocalesChange(targetLocales.filter(l => l !== next));
    }
  };

  return (
    <div className={clsx(showSource && 'space-y-4', className)}>
      {showSource && (
        <div>
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
          >
            {t('appScreen.sourceLanguage')}
          </label>
          <select
            id={selectId}
            value={sourceLocale}
            disabled={disabled}
            onChange={e => handleSourceChange(e.target.value)}
            className="w-full pl-2 pr-8 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm disabled:opacity-50"
          >
            {locales.map(loc => (
              <option key={loc.value} value={loc.value}>
                {getLocaleFlag(loc.value)} {loc.label}
              </option>
            ))}
          </select>
        </div>
      )}
      <TargetLocaleChecklist
        selected={targetLocales}
        onChange={onTargetLocalesChange}
        locales={locales}
        excludeLocales={[sourceLocale]}
        disabled={disabled}
        listClassName={targetListClassName}
      />
    </div>
  );
};

export default SourceTargetLocalePicker;
